import React, { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { api } from "@/lib/api";

const CSS = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;1,9..144,400&display=swap');
.pcc{position:relative;display:grid;grid-template-columns:1fr 330px;border-radius:26px;background:#EFEBE0;color:#12261D;border:1px solid rgba(18,38,29,.14);overflow:hidden;font-family:Inter,sans-serif}
.pcc *{box-sizing:border-box}
.pcc-body{padding:46px 50px;display:flex;flex-direction:column;justify-content:center}
.pcc-eyebrow{font-size:12px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:#93692B}
.pcc-h{font-family:Fraunces,'Times New Roman',serif;font-weight:400;font-size:62px;line-height:1;letter-spacing:-.025em;margin:16px 0 10px;color:#12261D;font-variation-settings:"opsz" 144}
.pcc-offer{font-family:Fraunces,'Times New Roman',serif;font-style:italic;font-weight:400;font-size:30px;line-height:1.15;color:#93692B;margin:0}
.pcc-p{margin:14px 0 0;font-size:15px;line-height:1.55;color:#6E6A5E;max-width:460px}
.pcc-meta{display:flex;flex-wrap:wrap;align-items:center;gap:16px 30px;margin-top:26px}
.pcc-time{display:flex;align-items:baseline;gap:18px;font-size:13px;color:#6E6A5E}
.pcc-time>span:first-child{font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase}
.pcc-u b{font-family:Fraunces,'Times New Roman',serif;font-weight:500;font-size:30px;color:#12261D;font-variant-numeric:tabular-nums;margin-right:4px}
.pcc-code{display:inline-flex;align-items:center;gap:12px;padding:12px 20px;border:1.5px solid #12261D;border-radius:999px;background:transparent;color:#12261D;cursor:pointer;font:600 14px/1 Inter,sans-serif;letter-spacing:.1em}
.pcc-code small{font:400 12px Inter,sans-serif;letter-spacing:0;color:#6E6A5E}
.pcc-code:hover{background:#12261D;color:#FAF8F3}.pcc-code:hover small{color:rgba(250,248,243,.7)}
.pcc-side{position:relative;margin:22px 22px 22px 0}
.pcc-photo{height:100%;min-height:310px;border-radius:154px 154px 16px 16px;background-color:#12261D;background-size:cover;background-position:center}
.pcc-badge{position:absolute;left:-30px;bottom:40px;width:116px;height:116px;border-radius:50%;background:#B8863B;color:#12261D;display:flex;flex-direction:column;align-items:center;justify-content:center;transform:rotate(-8deg);box-shadow:0 6px 16px rgba(18,38,29,.25)}
.pcc-badge small{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase}
.pcc-badge b{font-family:Fraunces,'Times New Roman',serif;font-weight:500;font-size:38px;line-height:1;letter-spacing:-.03em}
@media (max-width:760px){
.pcc{grid-template-columns:1fr}
.pcc-side{order:-1;margin:14px 14px 0}
.pcc-photo{min-height:190px;height:190px;border-radius:16px}
.pcc-badge{left:auto;right:12px;bottom:-26px;width:96px;height:96px}.pcc-badge b{font-size:30px}
.pcc-body{padding:34px 22px 26px}.pcc-h{font-size:38px}.pcc-offer{font-size:24px}
.pcc-time{gap:12px}.pcc-u b{font-size:24px}}
`;

const endOf = (until) => {
  const [y, m, d] = (until || "").split("-").map(Number);
  return y ? new Date(y, m - 1, d, 23, 59, 59) : null;
};
const two = (n) => String(n).padStart(2, "0");

export const PromoCard = () => {
  const [s, setS] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  if (!s || !s.promo_enabled || !s.promo_text) return null;
  const end = endOf(s.promo_until);
  const ms = end ? end - now : null;
  if (ms !== null && ms <= 0) return null;

  const m = /(\d{1,2})\s*%/.exec(s.promo_text);
  const pct = m ? m[1] : (s.promo_percent ? String(s.promo_percent) : "");
  const parts = s.promo_text.split(":");
  const split = parts.length > 1;
  const head = split ? parts[0].trim() : s.promo_text;
  const rest = split ? parts.slice(1).join(":").trim() : "";
  const offer = pct ? `${pct} % Rabatt bei Direktbuchung` : (rest || "Nur bei Direktbuchung");
  const sub = s.promo_code
    ? "Code im Buchungsformular eingeben, gilt nur hier auf atlas-apartment.de."
    : "Gilt nur bei Buchung direkt auf atlas-apartment.de.";

  const units = ms === null ? [] : [
    [Math.floor(ms / 86400000), "Tage"],
    [Math.floor(ms / 3600000) % 24, "Std"],
    [Math.floor(ms / 60000) % 60, "Min"],
    [Math.floor(ms / 1000) % 60, "Sek"],
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(s.promo_code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
  };

  return (
    <div data-testid="promo-card" className="pcc">
      <style>{CSS}</style>
      <div className="pcc-body">
        <span className="pcc-eyebrow">Aktion · Nur direkt</span>
        <h2 className="pcc-h">{head}</h2>
        <p className="pcc-offer">{offer}</p>
        <p className="pcc-p">{sub}</p>
        <div className="pcc-meta">
          {units.length > 0 && (
            <div className="pcc-time">
              <span>Endet in</span>
              {units.map(([v, l]) => (<span key={l} className="pcc-u"><b>{two(v)}</b>{l}</span>))}
            </div>
          )}
          {s.promo_code && (
            <button onClick={copy} className="pcc-code">
              {s.promo_code} {copied ? <Check size={15} /> : <Copy size={15} />}
              <small>{copied ? "Kopiert!" : "kopieren"}</small>
            </button>
          )}
        </div>
      </div>
      <div className="pcc-side">
        <div className="pcc-photo" style={s.hero_image ? { backgroundImage: `url(${s.hero_image})` } : undefined} />
        {pct && (<div className="pcc-badge"><small>nur direkt</small><b>−{pct}%</b></div>)}
      </div>
    </div>
  );
};
